/*
 * collection-filter-tweaks.js
 * ---------------------------------------------------------------------------
 * 1. Renders every filter widget title (.m-filter--widget-title) with the
 *    heading tag chosen in the section settings (default: h3).
 * 2. Hides filter widgets by title on the collections configured in the
 *    section setting "Hide filter widgets".
 *
 * Configuration is read from data attributes printed by
 * snippets/collection-sidebar.liquid on .m-filter--wrapper:
 *    data-filter-title-tag="h3"
 *    data-hidden-filters="Size, Brand"
 *
 * This file does not modify any original theme file behaviour. It runs before
 * the theme initialises the accordions (window "load"), so collapsible filter
 * groups keep working normally.
 */
(function () {
  "use strict";

  var WRAPPER_SELECTOR = ".m-filter--wrapper";
  var TITLE_SELECTOR = ".m-filter--widget-title";
  var WIDGET_SELECTOR =
    ".m-filter--widget, .m-accordion--item, .m-facets__group, .m-banner-promotion, .m-facet--group";
  var HIDDEN_CLASS = "m-filter--widget-hidden";
  var ALLOWED_TAGS = ["h2", "h3", "h4", "h5", "h6"];
  var observed = [];
  var processing = false;

  function normalize(value) {
    return (value || "")
      .replace(/\s+/g, " ")
      .replace(/\(\s*\d+\s*\)\s*$/, "")
      .trim()
      .toLowerCase();
  }

  function parseList(value) {
    if (!value) return [];
    return value
      .split(",")
      .map(function (item) {
        return normalize(item);
      })
      .filter(function (item) {
        return item.length > 0;
      });
  }

  function matches(node, selector) {
    if (!node || node.nodeType !== 1) return false;
    var fn =
      node.matches ||
      node.msMatchesSelector ||
      node.webkitMatchesSelector;
    return fn ? fn.call(node, selector) : false;
  }

  function closestWidget(node, wrapper) {
    var current = node;
    while (current && current !== wrapper) {
      if (matches(current, WIDGET_SELECTOR)) return current;
      current = current.parentElement;
    }
    return null;
  }

  /* ---- 1. Heading tag ---------------------------------------------------- */
  function upgradeTitles(wrapper, tag) {
    if (!tag) return;
    tag = String(tag).toLowerCase();
    if (ALLOWED_TAGS.indexOf(tag) === -1) return;

    var titles = wrapper.querySelectorAll(TITLE_SELECTOR);
    Array.prototype.forEach.call(titles, function (title) {
      var currentTag = title.tagName.toLowerCase();
      if (currentTag === tag) return;
      // Only swap heading elements, never buttons / divs used by other logic.
      if (!/^h[1-6]$/.test(currentTag)) return;

      var replacement = document.createElement(tag);
      Array.prototype.forEach.call(title.attributes, function (attr) {
        replacement.setAttribute(attr.name, attr.value);
      });
      replacement.innerHTML = title.innerHTML;
      title.parentNode.replaceChild(replacement, title);
    });
  }

  /* ---- 2. Hide widgets --------------------------------------------------- */
  function hideWidgets(wrapper, list) {
    if (!list.length) return;

    var titles = wrapper.querySelectorAll(TITLE_SELECTOR);
    Array.prototype.forEach.call(titles, function (title) {
      var key = normalize(title.textContent);
      if (!key || list.indexOf(key) === -1) return;

      var widget = closestWidget(title, wrapper) || title.parentElement;
      if (!widget || widget === wrapper) return;

      widget.classList.add(HIDDEN_CLASS);
      widget.setAttribute("data-filter-hidden", "true");
      widget.style.display = "none";
    });
  }

  /* ---- 3. Force text rendering (metafield filters sent as swatches) ------ */
  function getValueLabel(item) {
    var tooltip = item.querySelector(".m-tooltip__content");
    if (tooltip && tooltip.textContent.trim()) return tooltip.textContent.trim();

    var link = item.querySelector("a");
    if (link) {
      if (link.getAttribute("aria-label")) return link.getAttribute("aria-label").trim();
      if (link.getAttribute("title")) return link.getAttribute("title").trim();
      if (link.textContent.trim()) return link.textContent.trim();
    }

    var input = item.querySelector("input[type='checkbox'], input[type='radio']");
    if (input && input.value) return String(input.value).trim();

    var label = item.querySelector("label");
    if (label && label.textContent.trim()) return label.textContent.trim();

    return "";
  }

  function forceTextGroups(wrapper, list) {
    if (!list.length) return;

    var titles = wrapper.querySelectorAll(TITLE_SELECTOR);
    Array.prototype.forEach.call(titles, function (title) {
      var key = normalize(title.textContent);
      if (!key || list.indexOf(key) === -1) return;

      var widget = closestWidget(title, wrapper);
      if (!widget) return;

      var items = widget.querySelectorAll(".m-facet--color");
      if (!items.length) return;

      widget.classList.add("m-filter--force-text");

      Array.prototype.forEach.call(items, function (item) {
        if (item.querySelector(".m-facet--forced-label")) return;
        var text = getValueLabel(item);
        if (!text) return;

        var span = document.createElement("span");
        span.className = "m-facet--forced-label";
        span.textContent = text;

        var link = item.querySelector("a");
        (link || item).appendChild(span);
      });
    });
  }

  function processWrapper(wrapper) {
    var tag = wrapper.getAttribute("data-filter-title-tag") || "h3";
    var list = parseList(wrapper.getAttribute("data-hidden-filters"));
    var textList = parseList(wrapper.getAttribute("data-text-filters"));
    upgradeTitles(wrapper, tag);
    hideWidgets(wrapper, list);
    forceTextGroups(wrapper, textList);
    wrapper.setAttribute("data-filter-tweaks-ready", "true");
  }

  function run() {
    if (processing) return;
    processing = true;
    try {
      var wrappers = document.querySelectorAll(WRAPPER_SELECTOR);
      Array.prototype.forEach.call(wrappers, function (wrapper) {
        processWrapper(wrapper);
        observe(wrapper);
      });
    } catch (err) {
      console.error("collection-filter-tweaks:", err);
    }
    processing = false;
  }

  /* Filters are re-rendered by AJAX after filtering / paginating, so watch for
     new markup and re-apply the tweaks. */
  function observe(wrapper) {
    if (!window.MutationObserver) return;
    if (observed.indexOf(wrapper) !== -1) return;
    observed.push(wrapper);

    var timer = null;
    var observer = new MutationObserver(function () {
      if (processing) return;
      clearTimeout(timer);
      timer = setTimeout(function () {
        processing = true;
        try {
          processWrapper(wrapper);
        } catch (err) {
          console.error("collection-filter-tweaks:", err);
        }
        processing = false;
      }, 60);
    });

    observer.observe(wrapper, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  window.addEventListener("load", run);
  document.addEventListener("shopify:section:load", run);

  // Manual hook, e.g. MinimogFilterTweaks.refresh()
  window.MinimogFilterTweaks = { refresh: run };
})();
